import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { api } from '../../services/api';
import { statutLabels, statutColors } from '../../mocks/commandes';
import FactureModal from '../../components/feature/FactureModal';
import PaiementModal from '../../components/feature/PaiementModal';
import { checkPermission, PERMISSIONS } from '../../utils/permissions';
import { formatPrice } from '../../utils/format';

interface UserInfo { id: string; nom: string; role: string; }

const PERIODES = []; // If needed in future

export default function HistoriqueClients() {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'tout' | 'commandes' | 'achats'>('tout');
  const [factureData, setFactureData] = useState<any | null>(null);
  const [selectedVenteForPayment, setSelectedVenteForPayment] = useState<any | null>(null);
  const [entreprise, setEntreprise] = useState({
    nom: 'Atelier Bassoum Couture',
    slogan: "L'expérience du sur-mesure",
    adresse: 'Bamako, Mali',
    telephone: '',
    email: '',
    logoUrl: '',
    headerText: 'Facture / Reçu de paiement',
    footerText: 'Merci de votre confiance !',
    nif: '',
    rccm: '',
    devise: 'FCFA',
    couleurPrimaire: '#4a7c59',
  });
  const [clients, setClients] = useState<any[]>([]);
  const [history, setHistory] = useState<{ commandes: any[], ventes: any[] }>({ commandes: [], ventes: [] });
  const [trashClients, setTrashClients] = useState<any[]>([]);
  const [showTrash, setShowTrash] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('atelier_bassoum_token');
    const stored = localStorage.getItem('atelier_bassoum_user');
    if (stored && token) {
      setUser(JSON.parse(stored));
      fetchClients(token);
      fetchEntrepriseSettings(token);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  // FIX BUG #5: Charger les settings depuis l'API, pas le localStorage statique
  const fetchEntrepriseSettings = async (token: string) => {
    try {
      const data = await api.getSettings(token);
      const settings = {
        nom:             data.nom              || 'Atelier Bassoum Couture',
        slogan:          data.slogan           || '',
        adresse:         data.adresse          || '',
        telephone:       data.telephone        || '',
        email:           data.email            || '',
        logoUrl:         data.logo_url         || data.logoUrl || '',
        headerText:      data.header_text      || data.headerText || 'Facture / Reçu de paiement',
        footerText:      data.footer_text      || data.footerText || 'Merci de votre confiance !',
        nif:             data.nif              || '',
        rccm:            data.rccm             || '',
        devise:          data.devise           || 'FCFA',
        couleurPrimaire: data.couleur_primaire || data.couleurPrimaire || '#4a7c59',
      };
      setEntreprise(settings);
      // Synchroniser le localStorage pour les autres pages
      localStorage.setItem('atelier_bassoum_entreprise', JSON.stringify(settings));
    } catch (err) {
      console.error('Settings fetch failed, using localStorage fallback', err);
      const cached = localStorage.getItem('atelier_bassoum_entreprise');
      if (cached) {
        try { setEntreprise(JSON.parse(cached)); } catch { /* ignore */ }
      }
    }
  };

  const fetchClients = async (token: string) => {
    setLoading(true);
    try {
      const data = await api.getClients(token);
      setClients(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (clientId: string | number) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    setLoadingHistory(true);
    try {
      const data = await api.getClientHistory(token, clientId);
      setHistory(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchTrash = async () => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      const data = await api.getTrashClients(token);
      setTrashClients(data);
    } catch (err) {
      console.error(err);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSoftDelete = async (id: string | number) => {
    if (!window.confirm("Voulez-vous mettre ce client à la corbeille ?")) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.softDeleteClient(token, id);
      showToast("Client mis à la corbeille");
      if (selectedClientId === id) setSelectedClientId(null);
      fetchClients(token);
    } catch (err) {
      showToast("Erreur lors de la suppression", "error");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedClientIds.length === 0) return;
    if (!window.confirm(`Mettre ${selectedClientIds.length} clients à la corbeille ?`)) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      setLoading(true);
      for (const id of selectedClientIds) {
        await api.softDeleteClient(token, id);
      }
      showToast(`${selectedClientIds.length} clients supprimés`);
      setSelectedClientIds([]);
      if (selectedClientId && selectedClientIds.includes(String(selectedClientId))) {
         setSelectedClientId(null);
      }
      fetchClients(token);
    } catch (err) {
      showToast("Erreur lors de la suppression", "error");
      setLoading(false);
    }
  };

  const handleRestore = async (id: string | number) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.restoreClient(token, id);
      showToast("Client restauré");
      fetchClients(token);
      fetchTrash();
    } catch (err) {
      showToast("Erreur lors de la restauration", "error");
    }
  };

  const handleHardDelete = async (id: string | number) => {
    if (!window.confirm("Action irréversible. Supprimer définitivement ce client ?")) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.hardDeleteClient(token, id);
      showToast("Client supprimé définitivement");
      fetchTrash();
    } catch (err) {
      showToast("Erreur lors de la suppression définitive", "error");
    }
  };

  const formatWhatsAppMessage = (item: any, type: 'commande' | 'vente') => {
    const client = selectedClient;
    const biz = entreprise;
    let msg = `*${biz.nom}*\n`;
    
    if (type === 'commande') {
      msg += `Bonjour ${client?.nom},\nVoici le détail de votre commande :\n`;
      msg += `📍 Réf: ${item.code}\n`;
      msg += `👗 Modèle: ${item.modele_nom}\n`;
      msg += `💰 Total: ${formatPrice(item.total)}\n`;
      msg += `💵 Acompte: ${formatPrice(item.acompte)}\n`;
      msg += `⏳ Reste: ${formatPrice(item.reste)}\n`;
      if (item.date_livraison) msg += `📅 Livraison prévue: ${new Date(item.date_livraison).toLocaleDateString('fr-FR')}\n`;
    } else {
      msg += `Bonjour ${client?.nom},\nVoici votre reçu de vente pour l'achat de tissu :\n`;
      msg += `📍 Réf: VT-${item.id}\n`;
      msg += `🧣 Tissu: ${item.tissu_nom}\n`;
      msg += `📏 Quantité: ${item.quantite} ${item.tissu_unite || 'm'}\n`;
      msg += `💰 Total: ${formatPrice(item.total)}\n`;
    }
    
    msg += `\nMerci pour votre confiance !\n_${biz.slogan}_`;
    return encodeURIComponent(msg);
  };

  const handleWhatsAppShare = (item: any, type: 'commande' | 'vente') => {
    const phone = selectedClient?.telephone.replace(/\s+/g, '');
    if (!phone) {
        showToast("Aucun numéro de téléphone pour ce client", "error");
        return;
    }
    const cleanPhone = phone.startsWith('+') ? phone.replace('+', '') : `223${phone}`;
    const message = formatWhatsAppMessage(item, type);
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
  };

  const handleSolderVente = async (venteId: number, montantVerse: number, modePaiement: string) => {
    try {
        const token = localStorage.getItem('atelier_bassoum_token') || '';
        await api.updateVentePaiement(token, venteId, { montant_verse: montantVerse, mode_paiement: modePaiement });
        showToast("Paiement enregistré avec succès !");
        setSelectedVenteForPayment(null);
        if (selectedClientId) fetchHistory(selectedClientId);
    } catch (err: any) {
        showToast(`Erreur lors de la mise à jour: ${err.message || "Inconnue"}`, "error");
    }
  };

  useEffect(() => {
    if (selectedClientId) {
      fetchHistory(selectedClientId);
    }
  }, [selectedClientId]);

  const filteredClients = clients.filter(c =>
    !search || c.nom?.toLowerCase().includes(search.toLowerCase()) || c.telephone?.includes(search)
  );

  const toggleSelect = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setSelectedClientIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectedClient = clients.find(c => String(c.id) === String(selectedClientId));

  const totalDepense = selectedClient ? (Number(selectedClient.total_depense) || 0) : 0;
  const totalReste = history.commandes.reduce((acc, c) => acc + (Number(c.reste) || 0), 0);

  function fmt(n: any) {
    return formatPrice(n);
  }

  const mapToFacture = (item: any, type: 'commande' | 'vente') => {
    if (type === 'commande') {
      return {
        type: 'commande',
        data: {
          id: item.code,
          dateCommande: item.date_commande,
          dateLivraison: item.date_livraison,
          statut: item.statut,
          client: {
            id: String(item.client_id),
            nom: selectedClient?.nom || '',
            telephone: selectedClient?.telephone || ''
          },
          modele: item.modeles_summary || item.modele_nom || (item.articles && item.articles.length > 1 ? `${item.articles.length} articles` : (item.articles?.[0]?.modele_nom || 'Sur mesure')),
          vendeur: item.vendeur_nom || 'Atelier Bassoum',
          paiement: {
            total: Number(item.total),
            remise: Number(item.remise_globale || item.remise || 0),
            acompte: Number(item.acompte),
            reste: Number(item.reste),
            modePaiement: item.mode_paiement || 'Espèces',
            vendeur: item.vendeur_nom || 'Atelier Bassoum'
          },
          articles: item.articles ? item.articles.map((art: any) => ({
            ...art,
            tissu_nom: art.tissus?.[0]?.tissu_nom || art.tissuNom // Support pour FactureModal
          })) : []
        }
      };
    } else {
      return {
        type: 'vente',
        data: {
          id: `VT-${item.id}`,
          date: item.created_at,
          clientNom: selectedClient?.nom || item.client_nom || '',
          clientTelephone: selectedClient?.telephone || '',
          vendeur: item.vendeur_nom || 'Atelier Bassoum',
          total: Number(item.total_net || item.total),
          remise: Number(item.remise_globale || item.remise || 0),
          acompte: Number(item.acompte || 0),
          reste: Number(item.reste || 0),
          modePaiement: item.mode_paiement || 'Espèces',
          articles: item.articles ? item.articles.map((art: any) => ({
            tissuNom: art.tissu_nom,
            quantite: art.quantite,
            unite: art.tissu_unite || art.unite || 'm',
            prixUnitaire: art.prix_unitaire,
            total: art.total_ligne || art.total
          })) : [{
            tissuNom: item.tissu_nom,
            quantite: item.quantite,
            unite: item.tissu_unite || 'm',
            prixUnitaire: item.prix_unitaire,
            total: item.total
          }],
          montantRecu: Number(item.total),
          monnaie: 0
        }
      };
    }
  };

  function formatDate(d: string) {
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  if (!user) return null;

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && (
        <div className={`fixed top-6 right-6 z-50 px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top-5 border border-white/20 backdrop-blur-md ${
          toast.type === 'success' ? 'bg-green-600/90 text-white' : 'bg-red-600/90 text-white'
        }`}>
          <i className={toast.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} />
          <span className="text-sm font-bold">{toast.message}</span>
        </div>
      )}
      <div className="hidden md:block">
        <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      <div className="flex-1 flex overflow-hidden min-w-0 flex-col md:flex-row relative">
        {/* Left panel: client list */}
        <div className={`${selectedClientId ? 'hidden md:flex' : 'flex'} w-full md:w-72 flex-shrink-0 flex-col border-r border-gray-100 bg-white h-full`}>
          <div className="px-4 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-playfair font-bold text-base text-gray-900">Historique</h2>
            <div className="flex items-center gap-1">
              <button 
                  onClick={() => { fetchTrash(); setShowTrash(true); }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-red-500 transition-all cursor-pointer"
                  title="Corbeille"
              >
                  <i className="ri-delete-bin-7-line" />
              </button>
            </div>
          </div>
          <div className="px-4 py-3 border-b border-gray-50 flex-shrink-0">
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-search-line text-sm" />
              </div>
              <input
                id="search-clients-input"
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher…"
                title="Rechercher un client"
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-gray-50"
              />
            </div>
            {selectedClientIds.length > 0 && (
                <div className="mt-3 flex items-center justify-between bg-red-50 px-3 py-2 rounded-lg border border-red-100">
                    <span className="text-xs font-semibold text-red-600">{selectedClientIds.length} sélectionné(s)</span>
                    <button 
                        onClick={handleBulkDelete}
                        className="text-xs bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                    >
                        Supprimer
                    </button>
                </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto pb-24 md:pb-0">
            {filteredClients.map(client => {
              const totalActions = (Number(client.nb_commandes) || 0) + (Number(client.nb_ventes) || 0);
              const isSelected = selectedClientIds.includes(String(client.id));
              return (
                <div
                  key={client.id}
                  className={`w-full group text-left px-4 py-3.5 border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer flex items-center ${selectedClientId === client.id ? 'bg-foret-50 border-l-2 border-l-foret-500' : ''}`}
                  onClick={() => { setSelectedClientId(client.id); setActiveTab('tout'); }}
                >
                  <div className="mr-3" onClick={(e) => toggleSelect(e, String(client.id))}>
                      <input 
                        type="checkbox" 
                        checked={isSelected} 
                        readOnly 
                        title="Sélectionner ce client"
                        className="w-4 h-4 rounded border-gray-300 text-foret-500 focus:ring-foret-500 cursor-pointer pointer-events-none" 
                      />
                  </div>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-9 h-9 bg-foret-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-foret-700">
                        {(client.nom || 'Client').split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{client.nom || 'Sans nom'}</p>
                      <p className="text-xs text-gray-400">{client.telephone || 'Pas de numéro'}</p>
                    </div>
                    {totalActions > 0 && (
                      <div className="text-right flex-shrink-0 mr-2">
                        <p className="text-xs font-bold text-foret-600">{fmt(client.total_depense)}</p>
                        <p className="text-xs text-gray-400">{totalActions} achat{totalActions > 1 ? 's' : ''}</p>
                      </div>
                    )}
                  </div>
                  <button 
                      onClick={(e) => { e.stopPropagation(); handleSoftDelete(client.id); }}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:bg-red-100 hover:text-red-500 rounded-lg flex-shrink-0 transition-colors md:opacity-0 group-hover:opacity-100"
                      title="Supprimer"
                  >
                      <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right panel: history */}
        <div className={`${selectedClientId ? 'flex' : 'hidden md:flex'} flex-1 flex-col overflow-hidden bg-gray-50/30`}>
          {!selectedClient ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-user-3-line text-2xl text-gray-300" />
                </div>
                <p className="text-gray-400 text-sm">Sélectionnez un client pour voir son historique</p>
              </div>
            </div>
          ) : (
            <>
              {/* Client header */}
              <div className="bg-white border-b border-gray-100 px-4 md:px-6 py-4 flex-shrink-0">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <button 
                      onClick={() => setSelectedClientId(null)}
                      className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl bg-gray-50 text-gray-600"
                      title="Retour à la liste"
                    >
                      <i className="ri-arrow-left-line text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-foret-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm md:text-base">
                          {(selectedClient.nom || 'Client').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-playfair font-bold text-base md:text-lg text-gray-900 leading-tight">{selectedClient.nom}</h3>
                        <p className="text-xs md:text-sm text-gray-400 flex items-center gap-1.5 mt-0.5">
                          <i className="ri-phone-line text-xs" />
                          {selectedClient.telephone}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 lg:flex lg:items-center gap-3 md:gap-4 text-right">
                    <div className="bg-gray-50 p-2 md:p-0 md:bg-transparent rounded-xl md:rounded-none text-left md:text-right">
                      <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest">Total dépensé</p>
                      <p className="text-sm md:text-lg font-black text-foret-600">{fmt(totalDepense)}</p>
                    </div>
                    <div className="bg-gray-50 p-2 md:p-0 md:bg-transparent rounded-xl md:rounded-none text-left md:text-right">
                      <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest">Commandes</p>
                      <p className="text-sm md:text-lg font-black text-gray-800">{history.commandes.length}</p>
                    </div>
                    {totalReste > 0 && (
                      <div className="col-span-2 bg-red-50 px-3 py-2 md:py-1.5 rounded-xl border border-red-100 text-left md:text-right">
                        <p className="text-[10px] text-red-500 font-black uppercase tracking-widest leading-none mb-1">Reste à payer</p>
                        <p className="text-base md:text-lg font-black text-red-600">{fmt(totalReste)}</p>
                      </div>
                    )}
                    <div className="flex items-center gap-2 md:ml-2">
                      <button 
                          onClick={() => handleSoftDelete(selectedClient.id)}
                          className="flex-1 md:w-9 md:h-9 py-2 md:py-0 flex items-center justify-center rounded-xl bg-gray-50 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-all cursor-pointer border border-gray-100"
                          title="Mettre à la corbeille"
                      >
                          <i className="ri-delete-bin-line" />
                          <span className="md:hidden ml-2 text-[10px] font-bold">Supprimer</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mt-6 bg-gray-100 rounded-xl p-1 w-full md:w-fit overflow-x-auto no-scrollbar">
                  {([['tout', 'Tout'], ['commandes', 'Commandes'], ['achats', 'Ventes']] as const).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setActiveTab(key)}
                      className={`flex-1 md:flex-none px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer whitespace-nowrap ${activeTab === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                    >
                      {label}
                      {key === 'commandes' && history.commandes.length > 0 && (
                        <span className="ml-1.5 bg-foret-100 text-foret-700 rounded-full px-1.5 py-0.5">{history.commandes.length}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* History items */}
              <div className="flex-1 overflow-y-auto p-6">
                {loadingHistory ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foret-500"></div>
                  </div>
                ) : (
                  <div className="max-w-3xl mx-auto space-y-3">
                    {(activeTab === 'tout' || activeTab === 'commandes') && history.commandes.map(cmd => (
                      <div key={cmd.id} className="bg-white rounded-2xl border border-gray-100 p-5">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center">
                              {cmd.modele_image ? (
                                <img src={api.getProxyUrl(cmd.modele_image)} alt={cmd.modele_nom} className="w-full h-full object-cover object-top" />
                              ) : (
                                <i className="ri-image-line text-gray-300" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-bold text-gray-900">{cmd.modele_nom}</p>
                                <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${statutColors[cmd.statut] || 'bg-gray-100 text-gray-600'}`}>
                                  {statutLabels[cmd.statut] || cmd.statut}
                                </span>
                              </div>
                              <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                                <i className="ri-file-list-3-line" />
                                 {cmd.code} · {formatDate(cmd.date_commande)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <div className="text-right">
                              <p className="text-sm font-bold text-gray-900">{fmt(cmd.total)}</p>
                              {Number(cmd.remise) > 0 && (
                                <p className="text-xs text-amber-600 font-semibold flex items-center justify-end gap-1">
                                  <i className="ri-price-tag-3-line text-[10px]" /> Remise : -{fmt(cmd.remise)}
                                </p>
                              )}
                              {Number(cmd.reste) > 0 && (
                                <p className="text-xs text-red-400">Reste : {fmt(cmd.reste)}</p>
                              )}
                              {Number(cmd.reste) === 0 && (
                                <p className="text-xs text-green-500 font-semibold">Soldé</p>
                              )}
                            </div>
                            <button
                                onClick={() => setFactureData(mapToFacture(cmd, 'commande'))}
                                className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl bg-gray-100 text-gray-600 hover:bg-foret-50 hover:text-foret-600 cursor-pointer font-semibold whitespace-nowrap transition-colors"
                              >
                                <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-receipt-line" /></div>
                                Facture
                              </button>
                              <button
                                onClick={() => handleWhatsAppShare(cmd, 'commande')}
                                className="w-9 h-9 flex items-center justify-center rounded-xl bg-green-50 text-green-600 hover:bg-green-600 hover:text-white transition-all cursor-pointer border border-green-100"
                                title="Envoyer par WhatsApp"
                              >
                                <i className="ri-whatsapp-line transition-none" />
                              </button>
                          </div>
                        </div>

                        {/* Articles & Tissus Detail */}
                        <div className="space-y-2 mt-2 bg-gray-50 rounded-2xl p-3 border border-dashed border-gray-200">
                          {cmd.articles && Array.isArray(cmd.articles) ? (
                            cmd.articles.map((art: any, aidx: number) => (
                              <div key={aidx} className="flex flex-col gap-1 border-b border-gray-100 last:border-0 pb-2 last:pb-0">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-bold text-gray-800">{art.quantite} × {art.type_habit_nom || art.nom}</span>
                                  <span className="text-[10px] bg-white px-2 py-0.5 rounded border border-gray-100 text-gray-500">{art.modele_nom || 'Sans modèle'}</span>
                                </div>
                                {art.tissus && art.tissus.map((t: any, tidx: number) => (
                                  <div key={tidx} className="flex items-center gap-2 pl-3">
                                    <i className="ri-scissors-2-line text-[10px] text-gray-400" />
                                    <span className="text-[10px] text-gray-500 italic">
                                      {t.is_client_cloth ? 'Tissu client' : 'Tissu boutique'}: {t.tissu_nom || t.description} ({t.quantite}m)
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ))
                          ) : (
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-white border border-gray-100 flex items-center justify-center">
                                {cmd.tissu_image ? (
                                  <img src={api.getProxyUrl(cmd.tissu_image)} alt={cmd.tissu_nom} className="w-full h-full object-cover" />
                                ) : (
                                  <i className="ri-scissors-2-line text-gray-300 text-xs" />
                                )}
                              </div>
                              <div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tissu utilisé</p>
                                <p className="text-xs font-semibold text-gray-700">
                                  {cmd.tissu_nom || cmd.tissu_description || 'Tissu non précisé'}
                                  {cmd.quantite_tissu > 0 && <span className="text-gray-400 font-normal ml-1">({cmd.quantite_tissu}m)</span>}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {(activeTab === 'tout' || activeTab === 'achats') && history.ventes.map(vente => (
                      <div key={vente.uid || `${vente.source}_${vente.id}`} className="bg-white rounded-2xl border border-gray-100 p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 bg-or-100 flex items-center justify-center border border-or-200">
                              {vente.tissu_image ? (
                                <img src={api.getProxyUrl(vente.tissu_image)} alt={vente.tissu_nom} className="w-full h-full object-cover" />
                              ) : (
                                <i className="ri-shopping-bag-3-line text-or-600 text-lg" />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-bold text-gray-900">
                                {vente.source === 'new' ? 'Achat multi-articles' : `Achat tissu : ${vente.tissu_nom}`}
                              </p>
                              <p className="text-xs text-gray-400 flex items-center gap-1">
                                <i className="ri-receipt-line" />
                                {vente.code || `VT-${vente.id}`} · {formatDate(vente.created_at)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <div className="text-right">
                              <p className="text-sm font-bold text-gray-900">{fmt(vente.total)}</p>
                              {Number(vente.reste) > 0 ? (
                                <button 
                                  onClick={() => setSelectedVenteForPayment(vente)}
                                  className="mt-1 text-[9px] bg-red-50 text-red-600 font-bold px-2 py-0.5 rounded-full border border-red-100 hover:bg-red-500 hover:text-white transition-all cursor-pointer shadow-sm"
                                >
                                  Reste : {fmt(vente.reste)} (Solder?)
                                </button>
                              ) : (
                                <p className="text-xs text-green-500 font-semibold">Soldé · {vente.mode_paiement}</p>
                              )}
                            </div>
                            <button
                                onClick={() => setFactureData(mapToFacture(vente, 'vente'))}
                                className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl bg-gray-100 text-gray-600 hover:bg-or-50 hover:text-or-600 cursor-pointer font-semibold whitespace-nowrap transition-colors"
                              >
                                <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-receipt-line" /></div>
                                Facture
                              </button>
                          </div>
                        </div>

                        {/* Détail des articles */}
                        <div className="space-y-1 mt-2">
                          {vente.source === 'new' && vente.articles ? (
                            vente.articles.map((art: any, idx: number) => (
                              <div key={idx} className="flex items-center justify-between text-[11px] text-gray-600 bg-gray-50/50 rounded-lg px-3 py-1.5 border border-gray-100">
                                <span className="font-semibold">{art.tissu_nom}</span>
                                <span>{art.quantite} {art.tissu_unite || 'm'} × {formatPrice(art.prix_unitaire)} = <strong>{formatPrice(art.total_ligne)}</strong></span>
                              </div>
                            ))
                          ) : (
                            <div className="flex items-center justify-between text-[11px] text-gray-600 bg-gray-50/50 rounded-lg px-3 py-1.5 border border-gray-100">
                              <span>{vente.tissu_nom}</span>
                              <span>{vente.quantite} {vente.tissu_unite || 'm'} × {formatPrice(vente.prix_unitaire)} = <strong>{formatPrice(vente.total)}</strong></span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {history.commandes.length === 0 && history.ventes.length === 0 && !loadingHistory && (
                      <div className="text-center py-12">
                        <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <i className="ri-inbox-line text-2xl text-gray-300" />
                        </div>
                        <p className="text-gray-400 text-sm">Aucun historique pour ce client</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {factureData && (
        <FactureModal
          factureData={factureData}
          entreprise={entreprise}
          onClose={() => setFactureData(null)}
        />
      )}

      {/* Trash Modal */}
      {showTrash && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in" onClick={() => setShowTrash(false)}>
          <div className="bg-white rounded-[2.5rem] p-10 w-full max-w-2xl shadow-2xl flex flex-col max-h-[85vh] transform animate-in slide-in-from-bottom-10" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-8 overflow-hidden">
              <div className="flex items-center gap-5">
                <div className="w-16 h-16 bg-red-50 rounded-3xl flex items-center justify-center">
                    <i className="ri-delete-bin-7-line text-3xl text-red-500" />
                </div>
                <div>
                    <h3 className="font-playfair font-black text-3xl text-gray-900 tracking-tight">Corbeille</h3>
                    <p className="text-gray-400 font-medium mt-1">Gestion des clients supprimés</p>
                </div>
              </div>
              <button onClick={() => setShowTrash(false)} className="w-12 h-12 flex items-center justify-center rounded-2xl hover:bg-gray-100 text-gray-400 cursor-pointer transition-all active:scale-95" title="Fermer">
                <i className="ri-close-line text-2xl" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
              {trashClients.length === 0 ? (
                <div className="py-24 text-center">
                    <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <i className="ri-inbox-line text-5xl text-gray-200" />
                    </div>
                    <p className="text-gray-400 font-bold text-lg font-playfair">La corbeille est vide</p>
                    <p className="text-gray-300 text-sm mt-1">Aucun client à restaurer pour le moment</p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {trashClients.map(c => (
                    <div key={c.id} className="flex items-center justify-between p-5 rounded-[2rem] border border-gray-100 hover:border-red-100 hover:bg-red-50/10 transition-all group">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gray-900 rounded-2xl flex items-center justify-center text-white text-lg font-black shrink-0 shadow-lg">
                          {c.nom ? c.nom.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div>
                          <p className="font-black text-gray-900 leading-tight">{c.nom || 'Sans nom'}</p>
                          <p className="text-xs text-gray-400 font-bold mt-0.5">{c.telephone || 'Pas de numéro'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {checkPermission(PERMISSIONS.DELETE_CLIENT) && (
                          <button 
                              onClick={() => handleRestore(c.id)}
                              className="px-5 py-2.5 bg-foret-500 text-white rounded-xl text-xs font-black hover:bg-foret-600 transition-all cursor-pointer shadow-lg shadow-foret-100"
                          >
                              Restaurer
                          </button>
                        )}
                        {checkPermission(PERMISSIONS.DELETE_CLIENT) && (
                          <button 
                              onClick={() => handleHardDelete(c.id)}
                              className="w-10 h-10 bg-red-50 text-red-500 rounded-xl flex items-center justify-center hover:bg-red-500 hover:text-white transition-all cursor-pointer"
                              title="Supprimer définitivement"
                          >
                              <i className="ri-delete-bin-line" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="pt-8 border-t border-gray-100 mt-8 flex justify-end">
                <button 
                    onClick={() => setShowTrash(false)}
                    className="px-10 py-4 bg-gray-900 text-white rounded-2xl text-sm font-black hover:bg-black transition-all cursor-pointer shadow-xl active:scale-95"
                >
                    Fermer
                </button>
            </div>
          </div>
        </div>
      )}

      {selectedVenteForPayment && (
        <PaiementModal
          reste={selectedVenteForPayment.reste}
          onValider={(montant, mode) => handleSolderVente(selectedVenteForPayment.id, montant, mode)}
          onClose={() => setSelectedVenteForPayment(null)}
        />
      )}
    </div>
  );
}
